import APM, { LogActionType } from 'Common/APM';
import MXCache from 'Common/CacheManager/MXCache';
import { AppMode } from 'Common/Enums/metadata.enum';
import ContactBody from 'Common/Models/contact-body.model';
import MessageHistoryItem from 'Common/Models/message-history-item.model';
import RecentContactReduxState from 'Common/Models/recent-contacts.redux-state';
import RecentContactModel from 'Common/Models/recentcontacts.model';
import {
    ContactBoxMode,
    SelectedContactModel,
} from 'Common/Models/selected-contact.model';
import MXLogger from 'Common/MXLogger';
import { orderBy } from 'lodash';

const getSelectedContactsFromSessionStorage = (): SelectedContactModel[] => {
    try {
        const contacts = MXCache.GetCacheItem(
            MXCache.OpenedContactsKey
        ) as SelectedContactModel[];
        if (contacts && contacts.length) {
            return contacts;
        }
    } catch (error) {
        MXLogger.error(error);
    }
    return [];
    // try {
    //     const contactsFromStorage = MXCache.GetCacheItem(
    //         MXCache.RecentContactsOpenedKey
    //     ) as RecentContactModel[];
    //     let contacts = contactsFromStorage?.map(
    //         (item) => new RecentContactModel(item).toSelectedContact(null) // converting to Contact Model
    //     );
    //     if (contacts?.length) {
    //         return contacts;
    //     }
    // } catch (error) {
    //     MXLogger.error(error);
    // }
    // return [];
};

const getOpenedRecentContactsFromSessionStorage = (): RecentContactModel[] => {
    try {
        const contactsFromStorage = MXCache.GetCacheItem(
            MXCache.RecentContactsOpenedKey
        ) as RecentContactModel[];
        const contacts = contactsFromStorage?.map(
            (item) => new RecentContactModel(item)
        );
        if (contacts?.length) {
            return contacts;
        }
    } catch (error) {
        MXLogger.error(error);
    }
    return [];
};

const getDefaultReduxState = () => {
    const state = new RecentContactReduxState();
    state.SelectedRecentContacts = getSelectedContactsFromSessionStorage();
    state.OpenedRecentContacts = getOpenedRecentContactsFromSessionStorage();
    try {
        APM.logAction(LogActionType.REDUX_CACHE_HYDRATED, {
            fromCache: state.SelectedRecentContacts.length > 0,
            selectedCount: state.SelectedRecentContacts.length,
            openedCount: state.OpenedRecentContacts.length,
            selectedIds: state.SelectedRecentContacts.map(
                (c) => c?.id ?? 'none'
            ).join(','),
            selectedOpps: state.SelectedRecentContacts.map(
                (c) => c?.opportunityInstanceId ?? 'none'
            ).join(','),
            selectedNames: state.SelectedRecentContacts.map(
                (c) => c?.displayName ?? 'none'
            ).join(','),
        });
    } catch (e) {
        MXLogger.error(e);
    }
    return state;
};

const RecentContactsReducer = (
    state: RecentContactReduxState = getDefaultReduxState(),
    action: {
        type: string;
        contacts: RecentContactModel[];
        contact: RecentContactModel;
        App: AppMode;
        contactBoxMode: ContactBoxMode;
        message: MessageHistoryItem;
        reorder: boolean;
        isSystemUserOnly?: boolean;
        refreshTrigger: boolean;
        opportunityInstanceId: string;
        contactUpdates: Partial<ContactBody>;
    }
) => {
    switch (action.type) {
        case 'SET_RECENT_CONTACTS':
            return setRecentContactAndUpdateOpenedContact(
                state,
                action.contacts,
                action.isSystemUserOnly
            );
        case 'SET_LOAD_STATE_FALSE':
            return {
                ...state,
                RecentContacts: [],
                isLoaded: false,
                SearchMetadata: null,
            };
        case 'SELECT_CONTACT':
            return selectContact(state, action.contact, action.App, undefined);
        case 'SELECT_CONTACT_MINIMIZED':
            return selectContact(
                state,
                action.contact,
                action.App,
                undefined,
                ContactBoxMode.Minimized,
                action.isSystemUserOnly
            );
        case 'SET_MODE_CONTACT_BOX':
            return setModeContactBox(
                state,
                action.contact,
                action.contactBoxMode
            );
        case 'REMOVE_CONTACT_SELECTION':
            return removeContactFromSelection(
                state,
                action.contact,
                action.App
            );
        case 'SELECT_CONTACT_WITH_MESSAGE':
            return selectContact(
                state,
                action.contact,
                action.App,
                action.message
            );
        case 'CHANGE_CONTACT_DATA':
            return changeContact(state, action.contact);
        case 'ADD_CONTACT':
            return addContact(state, action.contact);
        case 'SET_UNREAD_ONLY':
            return setUnreadOnly(state, action.contact);
        case 'SET_STATUS':
            return setStatus(state, action.contact);
        case 'SET_READ_POINTER_ONLY':
            return setReadPointer(state, action.contact);
        case 'SET_RECENT_CONTACTS_WITH_SELECTED_CONTACT':
            return {
                ...state,
                RecentContacts:
                    deduplicateAndOrderContactsByTime(action.contacts) ?? [],
                SelectedRecentContacts: action.contact
                    ? [action.contact.toSelectedContact(null)]
                    : [],
                isLoaded: true,
            };
        case 'SET_CARTER_CONTACTS':
            return {
                ...state,
                CarterContact: action.contact,
            };
        case 'SELECT_SEARCHED_CONTACT':
            return selectSearchedContact(
                state,
                action.contact,
                action.App,
                undefined,
                ContactBoxMode.Maximized
            );
        case 'REMOVE_SEARCHED_CONTACT':
            return removeSearchedContact(state, action.contact);
        case 'EMPTY_SELECTED_CONTACTS':
            return emptySelectedContacts(state);
        case 'UPDATE_OPENED_RECENT_CONTACT':
            return updateOpenedRecentContact(state, action.contact);
        case 'UPDATE_APP_MODE':
            return {
                ...state,
                appMode: action.App,
            };
        case 'TRIGGER_CONTACT_REFRESH':
            return {
                ...state,
                refreshTrigger: action.refreshTrigger,
            };
        case 'UPDATE_RECENT_CONTACT':
            return updateRecentContact(
                state,
                action.opportunityInstanceId,
                action.contactUpdates
            );
        case 'REMOVE_CONTACT_AND_RESELECT':
            return removeContactAndReselect(state, action.contact, action.App);
        default:
            return state;
    }
};

let emptySelectedContacts = (state: RecentContactReduxState) => {
    saveContactsToCache([]);
    saveOpenedRecentContacts([]);
    return {
        ...state,
        SelectedRecentContacts: [],
        OpenedRecentContacts: [],
    };
};

let updateRecentContact = (
    state: RecentContactReduxState,
    opportunityInstanceId: string,
    contactUpdates: Partial<ContactBody>
): RecentContactReduxState => {
    const updateContactInList = (contacts: RecentContactModel[]) =>
        contacts.map((item) => {
            if (item.contact.opportunityInstanceId === opportunityInstanceId) {
                return new RecentContactModel({
                    ...item,
                    contact: {
                        ...item.contact,
                        ...contactUpdates,
                    },
                });
            }
            return item;
        });

    const updatedList = state.RecentContacts?.length
        ? updateContactInList(state.RecentContacts)
        : (state.RecentContacts ?? []);
    const RecentContacts = deduplicateAndOrderContactsByTime(updatedList);

    const OpenedRecentContacts: RecentContactModel[] = state
        .OpenedRecentContacts?.length
        ? updateContactInList(state.OpenedRecentContacts)
        : state.OpenedRecentContacts;

    if (state.OpenedRecentContacts?.length) {
        saveOpenedRecentContacts(OpenedRecentContacts);
    }
    if (state.appMode !== AppMode.CarterMarvin) {
        MXCache.SetCacheItem(MXCache.RecentContacts, RecentContacts, 600);
    }
    return {
        ...state,
        RecentContacts,
        OpenedRecentContacts,
    };
};

let updateOpenedRecentContact = (
    state: RecentContactReduxState,
    contact: RecentContactModel
) => {
    const OpenedRecentContacts = state.OpenedRecentContacts?.map((orc) => {
        if (
            orc.contact.opportunityInstanceId ===
            contact.contact.opportunityInstanceId
        ) {
            const temp = orc;
            temp.unreadMessages = orc.unreadMessages + 1;

            return temp;
        }
        return orc;
    });
    // !isSystemUserOnly &&
    saveOpenedRecentContacts(OpenedRecentContacts);
    return {
        ...state,
        OpenedRecentContacts,
    };
};

let setRecentContactAndUpdateOpenedContact = (
    state: RecentContactReduxState,
    contacts: RecentContactModel[],
    isSystemUserOnly?: boolean
) => {
    const newContacts = deduplicateAndOrderContactsByTime(contacts);
    const OpenedRecentContacts = state.OpenedRecentContacts?.map((orc) => {
        const contactItem = newContacts.find(
            (contact) =>
                contact.contact.opportunityInstanceId ===
                orc.contact.opportunityInstanceId
        );
        if (contactItem) {
            return contactItem;
        }
        return orc;
    });
    !isSystemUserOnly && saveOpenedRecentContacts(OpenedRecentContacts);
    // don't store the recent contact in carter only MArvin notification as the domain between widget and carter is same
    if (state.appMode !== AppMode.CarterMarvin) {
        MXCache.SetCacheItem(MXCache.RecentContacts, newContacts);
    }
    return {
        ...state,
        RecentContacts: newContacts,
        OpenedRecentContacts,
        isLoaded: true,
        SearchMetadata: null,
    };
};

const getInsertIndexByTime = (
    contacts: RecentContactModel[],
    timestamp: number,
    startFrom = 0
): number => {
    let low = startFrom;
    let high = contacts.length;
    while (low < high) {
        const mid = (low + high) >>> 1;
        const ts = contacts[mid]?.message?.recentContactTimeStamp ?? 0;
        if (ts >= timestamp) {
            low = mid + 1;
        } else {
            high = mid;
        }
    }
    return low;
};

const getContactCompositeKey = (c: RecentContactModel): string =>
    `${c.contact?.contactId ?? ''}|${c.contact?.opportunityInstanceId ?? ''}`;

/**
 * Upsert contacts by contactId + opportunityInstanceId so the same contact can
 * appear multiple times (once per opportunity). Matches existing row when both
 * contactId and opportunityInstanceId are the same.
 */
const upsertContactBasedTime = (
    existingContacts: RecentContactModel[],
    contactsToUpsert: RecentContactModel | RecentContactModel[]
): RecentContactModel[] => {
    let result = [...existingContacts];
    const contactsArray = Array.isArray(contactsToUpsert)
        ? contactsToUpsert
        : [contactsToUpsert];

    for (const contact of contactsArray) {
        const contactId = contact.contact?.contactId;
        const opportunityInstanceId = contact.contact?.opportunityInstanceId;
        if (!opportunityInstanceId) continue;

        const newContact = new RecentContactModel(contact);
        const isSystemUser = newContact.contact?.isContactSystemUser === true;

        let existingIndex = -1;
        for (let i = 0; i < result.length; i++) {
            const r = result[i].contact;
            if (
                r?.contactId === contactId &&
                r?.opportunityInstanceId === opportunityInstanceId
            ) {
                existingIndex = i;
                break;
            }
        }

        const newTime = newContact?.message?.recentContactTimeStamp ?? 0;

        if (existingIndex >= 0) {
            const existingTime =
                result[existingIndex]?.message?.recentContactTimeStamp ?? 0;
            if (newTime < existingTime) continue;
            result.splice(existingIndex, 1);
        }

        let insertIndex: number;
        if (isSystemUser) {
            insertIndex = 0;
        } else {
            const systemUserIndex = result.findIndex(
                (c) => c.contact?.isContactSystemUser === true
            );
            const startFrom = systemUserIndex >= 0 ? systemUserIndex + 1 : 0;
            insertIndex = getInsertIndexByTime(result, newTime, startFrom);
        }
        result.splice(insertIndex, 0, newContact);
    }

    return result;
};

/**
 * Ensures no duplicates by (contactId, opportunityInstanceId). Keeps one row
 * per pair, preferring the most recent by timestamp. Same contact can appear
 * multiple times (once per opportunity).
 */
const deduplicateAndOrderContactsByTime = (
    contacts: RecentContactModel[]
): RecentContactModel[] => {
    if (!contacts?.length) return contacts ?? [];
    // Sort by time descending first so we keep the most recent when deduping
    const systemUser = contacts.find(
        (c) => c.contact?.isContactSystemUser === true
    );
    const rest = contacts.filter(
        (c) => c.contact?.isContactSystemUser !== true
    );
    rest.sort((a, b) => {
        const aTime = a.message?.recentContactTimeStamp ?? 0;
        const bTime = b.message?.recentContactTimeStamp ?? 0;
        return bTime - aTime;
    });
    const sorted = systemUser ? [systemUser, ...rest] : rest;

    const seen = new Set<string>();
    const deduped: RecentContactModel[] = [];
    for (const c of sorted) {
        const key = getContactCompositeKey(c);
        if (!c.contact?.opportunityInstanceId || seen.has(key)) continue;
        seen.add(key);
        deduped.push(c);
    }
    return deduped;
};

let selectContact = (
    state: RecentContactReduxState,
    contact: RecentContactModel,
    appMode: AppMode,
    message: MessageHistoryItem | undefined,
    mode: ContactBoxMode = ContactBoxMode.Maximized,
    isSystemUserOnly?: boolean
): RecentContactReduxState => {
    const sc = state?.SelectedRecentContacts
        ? state.SelectedRecentContacts
        : [];
    const oc = state?.OpenedRecentContacts ? state.OpenedRecentContacts : [];
    let recentContacts = state.RecentContacts ?? [];
    const index = recentContacts.findIndex((r) =>
        r.isSameRecentContact(contact)
    );
    let selectedContacts = [] as any;
    let openedRecentContacts = [] as RecentContactModel[];
    let SearchMetadata = null as any;
    if (message) {
        SearchMetadata = {
            IsMessageSearch: true,
            MessageSearched: message,
        };
    }
    if (index > -1) {
        recentContacts[index].unreadMessages =
            mode === ContactBoxMode.Maximized
                ? 0
                : recentContacts[index].unreadMessages;
    } else {
        recentContacts = upsertContactBasedTime(recentContacts, contact);
    }
    if (
        appMode === AppMode.FullScreen ||
        appMode === AppMode.MarvinFullScreen
    ) {
        selectedContacts = [contact.toSelectedContact(SearchMetadata, mode)];
        openedRecentContacts = [contact];
    } else {
        const selectedContactIndex = sc.findIndex(
            (r) =>
                r.opportunityInstanceId ===
                contact.contact.opportunityInstanceId
        );
        if (selectedContactIndex === -1) {
            if (
                appMode === AppMode.MarvinWidget ||
                appMode === AppMode.LsqMobile
            ) {
                if (sc.length > 0) {
                    sc.pop();
                }
            } else if (sc.length > 1) {
                sc.pop();
                oc.pop();
            }

            selectedContacts = [
                contact.toSelectedContact(SearchMetadata, mode),
                ...sc,
            ];
            if (
                appMode === AppMode.MarvinWidget ||
                appMode === AppMode.LsqMobile
            ) {
                openedRecentContacts = [contact];
            } else {
                openedRecentContacts = [contact, ...oc];
            }
        } else {
            if (mode !== ContactBoxMode.Minimized) {
                sc[selectedContactIndex].Mode = mode;
            }
            sc[selectedContactIndex].SearchMetadata = SearchMetadata;
            if (
                appMode === AppMode.MarvinWidget ||
                appMode === AppMode.LsqMobile
            ) {
                selectedContacts = [sc[selectedContactIndex]];
            } else {
                selectedContacts = [...sc];
            }
        }
    }

    !isSystemUserOnly && saveContactsToCache(selectedContacts);
    !isSystemUserOnly && saveOpenedRecentContacts(openedRecentContacts);
    if (state.appMode !== AppMode.CarterMarvin) {
        MXCache.SetCacheItem(MXCache.RecentContacts, state.RecentContacts, 600);
    }
    return {
        ...state,
        RecentContacts: recentContacts,
        SelectedRecentContacts: selectedContacts,
        OpenedRecentContacts: openedRecentContacts,
    };
};

let selectSearchedContact = (
    state: RecentContactReduxState,
    contact: RecentContactModel,
    appMode: AppMode,
    message: MessageHistoryItem | undefined,
    mode: ContactBoxMode = ContactBoxMode.Maximized
): RecentContactReduxState => {
    const sc =
        state && state.SelectedRecentContacts
            ? state.SelectedRecentContacts
            : [];
    let orc = state?.OpenedRecentContacts ? state.OpenedRecentContacts : [];
    const recentContacts = state.RecentContacts ?? [];
    const index = orc.findIndex((r) => r.isSameRecentContact(contact));
    let selectedContacts = [] as any;
    let SearchMetadata = null as any;
    if (message) {
        SearchMetadata = {
            IsMessageSearch: true,
            MessageSearched: message,
        };
    }
    if (index > -1 && mode === ContactBoxMode.Maximized) {
        // UnreadMessage = 0 for searchedRecentContacts
        orc[index].unreadMessages = 0;
    } else {
        if (orc.length > 1) {
            orc.pop();
        }
        if (
            appMode === AppMode.MarvinWidget ||
            appMode === AppMode.LsqMobile
        ) {
            orc = [contact];
        } else {
            orc = [contact, ...orc];
        }
    }

    // make UnreadMessage = 0 for main RecentContact also, if present
    const indexInRecentContactList = recentContacts.findIndex((r) =>
        r.isSameRecentContact(contact)
    );
    if (indexInRecentContactList > -1) {
        recentContacts[indexInRecentContactList].unreadMessages = 0;
    }

    if (
        appMode === AppMode.FullScreen ||
        appMode === AppMode.MarvinFullScreen
    ) {
        selectedContacts = [contact.toSelectedContact(SearchMetadata, mode)];
    } else {
        const selectedContactIndex = sc.findIndex(
            (r) =>
                r.opportunityInstanceId ===
                contact.contact.opportunityInstanceId
        );
        if (selectedContactIndex === -1) {
            if (
                appMode === AppMode.MarvinWidget ||
                appMode === AppMode.LsqMobile
            ) {
                selectedContacts = [
                    contact.toSelectedContact(SearchMetadata, mode),
                ];
            } else {
                if (sc.length > 1) {
                    sc.pop();
                    orc.pop();
                }
                selectedContacts = [
                    contact.toSelectedContact(SearchMetadata, mode),
                    ...sc,
                ];
            }
        } else {
            if (mode !== ContactBoxMode.Minimized) {
                sc[selectedContactIndex].Mode = mode;
            }
            sc[selectedContactIndex].SearchMetadata = SearchMetadata;
            if (
                appMode === AppMode.MarvinWidget ||
                appMode === AppMode.LsqMobile
            ) {
                selectedContacts = [sc[selectedContactIndex]];
            } else {
                selectedContacts = [...sc];
            }
        }
    }

    saveOpenedRecentContacts(orc);
    saveContactsToCache(selectedContacts);

    // if RecentContact is already present in the list
    if (indexInRecentContactList > -1) {
        return {
            ...state,
            RecentContacts: recentContacts,
            SelectedRecentContacts: selectedContacts,
            OpenedRecentContacts: orc,
        };
    }

    return {
        ...state,
        RecentContacts: recentContacts,
        SelectedRecentContacts: selectedContacts,
        OpenedRecentContacts: orc,
    };
};

// const isSearchedContact = (
//     state: RecentContactReduxState,
//     contact: RecentContactModel
// ) => {
//     const searchedRecentContacts = state.SearchedRecentContacts ?? [];
//     const index = searchedRecentContacts.findIndex(
//         (item) =>
//             item.contact.opportunityInstanceId ===
//             contact.contact.opportunityInstanceId
//     );
//     if (index > -1) {
//         return true;
//     }
//     return false;
// };

let setModeContactBox = (
    state: RecentContactReduxState,
    contact: RecentContactModel,
    mode: ContactBoxMode
): RecentContactReduxState => {
    const sc =
        state && state.SelectedRecentContacts
            ? state.SelectedRecentContacts
            : [];
    const index = sc.findIndex(
        (r) => r.opportunityInstanceId === contact.contact.opportunityInstanceId
    );

    if (index > -1) {
        sc[index].Mode = mode;
        saveContactsToCache(sc);
        return {
            ...state,
            SelectedRecentContacts: sc,
        };
    }
    return state;
};

let removeContactAndReselect = (
    state: RecentContactReduxState,
    contactToRemove: RecentContactModel,
    appMode: AppMode
): RecentContactReduxState => {
    const { contactId, opportunityInstanceId } = contactToRemove.contact;
    const filteredContacts = (state.RecentContacts ?? []).filter(
        (item) =>
            item.contact.contactId !== contactId ||
            item.contact.opportunityInstanceId !== opportunityInstanceId
    );

    const sc =
        state && state.SelectedRecentContacts
            ? [...state.SelectedRecentContacts]
            : [];
    const oc = state?.OpenedRecentContacts
        ? [...state.OpenedRecentContacts]
        : [];
    const wasSelected =
        sc.findIndex(
            (r) =>
                r.opportunityInstanceId ===
                contactToRemove.contact.opportunityInstanceId
        ) > -1;

    const scIndex = sc.findIndex(
        (r) =>
            r.opportunityInstanceId ===
            contactToRemove.contact.opportunityInstanceId
    );
    if (scIndex > -1) {
        sc.splice(scIndex, 1);
    }
    const ocIndex = oc.findIndex(
        (r) =>
            r.contact.opportunityInstanceId ===
            contactToRemove.contact.opportunityInstanceId
    );
    if (ocIndex > -1) {
        oc.splice(ocIndex, 1);
    }

    saveOpenedRecentContacts(oc);
    saveContactsToCache(sc);
    if (state.appMode !== AppMode.CarterMarvin) {
        MXCache.SetCacheItem(MXCache.RecentContacts, filteredContacts);
    }

    const intermediateState: RecentContactReduxState = {
        ...state,
        RecentContacts: filteredContacts,
        SelectedRecentContacts: sc,
        OpenedRecentContacts: oc,
    };

    if (wasSelected && filteredContacts.length > 0) {
        return selectContact(
            intermediateState,
            filteredContacts[0],
            appMode,
            undefined,
            ContactBoxMode.Maximized
        );
    }

    return intermediateState;
};

let removeContactFromSelection = (
    state: RecentContactReduxState,
    contact: RecentContactModel,
    appMode: AppMode
): RecentContactReduxState => {
    const sc =
        state && state.SelectedRecentContacts
            ? state.SelectedRecentContacts
            : [];
    const oc = state?.OpenedRecentContacts ? state.OpenedRecentContacts : [];
    const index = sc.findIndex(
        (r) => r.opportunityInstanceId === contact.contact.opportunityInstanceId
    );
    if (index > -1) {
        sc.splice(index, 1);
        saveContactsToCache(sc);
    }
    const openRCIndex = oc.findIndex(
        (r) =>
            r.contact.opportunityInstanceId ===
            contact.contact.opportunityInstanceId
    );
    if (openRCIndex > -1) {
        oc.splice(openRCIndex, 1);
        saveOpenedRecentContacts(oc);
    }
    return {
        ...state,
        SelectedRecentContacts: sc,
        OpenedRecentContacts: oc,
    };
};
let changeContact = (
    state: RecentContactReduxState,
    contact: RecentContactModel
): RecentContactReduxState => {
    const OpenedRecentContacts = state.OpenedRecentContacts?.map((orc) => {
        if (
            orc.contact.opportunityInstanceId ===
            contact.contact.opportunityInstanceId
        ) {
            return contact;
        }
        return orc;
    });
    // !isSystemUserOnly &&
    saveOpenedRecentContacts(OpenedRecentContacts);
    const recentContacts = upsertContactBasedTime(
        state.RecentContacts ?? [],
        contact
    );
    if (state.appMode !== AppMode.CarterMarvin) {
        MXCache.SetCacheItem(MXCache.RecentContacts, recentContacts, 600);
    }
    return {
        ...state,
        RecentContacts: recentContacts,
        OpenedRecentContacts,
    };
};

let setUnreadOnly = (
    state: RecentContactReduxState,
    contact: RecentContactModel
): RecentContactReduxState => {
    if (state.RecentContacts) {
        const index = state.RecentContacts.findIndex((r) =>
            r.isSameRecentContact(contact)
        );
        if (index !== -1) {
            const rcontacts = [...state.RecentContacts];
            rcontacts[index].unreadMessages = contact.unreadMessages;
            return {
                ...state,
                RecentContacts: rcontacts,
            };
        }
    }
    const OpenedRecentContacts = state.OpenedRecentContacts?.map((orc) => {
        if (
            orc.contact.opportunityInstanceId ===
            contact.contact.opportunityInstanceId
        ) {
            const temp = orc;
            temp.unreadMessages = contact.unreadMessages;

            return temp;
        }
        return orc;
    });
    if (state.appMode !== AppMode.CarterMarvin) {
        MXCache.SetCacheItem(MXCache.RecentContacts, state.RecentContacts, 600);
    }
    saveOpenedRecentContacts(OpenedRecentContacts);
    return state;
};

let setStatus = (
    state: RecentContactReduxState,
    contact: RecentContactModel
): RecentContactReduxState => {
    if (state.RecentContacts) {
        const index = state.RecentContacts.findIndex((r) =>
            r.isSameRecentContact(contact)
        );
        if (index !== -1) {
            const rcontacts = [...state.RecentContacts];
            rcontacts[index].contact.status = 2;
            return {
                ...state,
                RecentContacts: rcontacts,
            };
        }
    }
    if (state.appMode !== AppMode.CarterMarvin) {
        MXCache.SetCacheItem(MXCache.RecentContacts, state.RecentContacts, 600);
    }
    return state;
};

let setReadPointer = (
    state: RecentContactReduxState,
    contact: RecentContactModel
): RecentContactReduxState => {
    if (state.RecentContacts) {
        const index = state.RecentContacts.findIndex((r) =>
            r.isSameRecentContact(contact)
        );
        if (index !== -1) {
            const rcontacts = [...state.RecentContacts];
            rcontacts[index].readPointer = contact.readPointer;
            return {
                ...state,
                RecentContacts: rcontacts,
            };
        }
        // check Opened Contacts
        const Openedindex = state.OpenedRecentContacts.findIndex((r) =>
            r.isSameRecentContact(contact)
        );
        if (Openedindex !== -1) {
            const openedcontacts = [...state.OpenedRecentContacts];
            openedcontacts[Openedindex].readPointer = contact.readPointer;
            // !isSystemUserOnly &&
            saveOpenedRecentContacts(openedcontacts);
            return {
                ...state,
                OpenedRecentContacts: openedcontacts,
            };
        }
    }
    if (state.appMode !== AppMode.CarterMarvin) {
        MXCache.SetCacheItem(MXCache.RecentContacts, state.RecentContacts, 600);
    }
    return state;
};

let addContact = (
    state: RecentContactReduxState,
    contact: RecentContactModel | RecentContactModel[]
): RecentContactReduxState => {
    const recentContacts = upsertContactBasedTime(
        state.RecentContacts ?? [],
        contact
    );
    if (state.appMode !== AppMode.CarterMarvin) {
        MXCache.SetCacheItem(MXCache.RecentContacts, recentContacts, 600);
    }
    return {
        ...state,
        RecentContacts: recentContacts,
        isLoaded: true,
    };
};

let removeSearchedContact = (
    state: RecentContactReduxState,
    contact: RecentContactModel
): RecentContactReduxState => {
    const openedRecentContacts = state.OpenedRecentContacts;
    if (openedRecentContacts) {
        const sRecentContacts = openedRecentContacts.filter(
            (r) => !r.isSameRecentContact(contact)
        );

        return {
            ...state,
            OpenedRecentContacts: sRecentContacts,
        };
    }

    return state;
};

let saveContactsToCache = (sc: SelectedContactModel[]) => {
    const scc = JSON.parse(JSON.stringify(sc)) as SelectedContactModel[];
    MXCache.SetCacheItem(
        MXCache.OpenedContactsKey,
        scc.map((i) => ({
            ...i,
            SearchMetadata: null,
        })),
        120
    );
};

let saveOpenedRecentContacts = (oc: RecentContactModel[]) => {
    const occ = JSON.parse(JSON.stringify(oc)) as RecentContactModel[];
    MXCache.SetCacheItem(
        MXCache.RecentContactsOpenedKey,
        occ.map((i) => ({
            ...i,
        })),
        120
    );
};

export default RecentContactsReducer;
